import React, { useEffect, useMemo, useState } from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { selectTimeSlot } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/select-time-slot';
import { ResponseDto } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/select-time-slot/dto/ResponseDto';
import { saleAgentTimeSlots } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots';
import { RequestDto } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots/dto/RequestDto';
import {
    ResponseDto as GetTimeSlotsResponseDto,
} from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots/dto/ResponseDto';

import DateOfServiceEngineerVisitWrapper, {
    ESaleAgentClientName,
    TReturnType,
} from '@r1-frontend/service-engineer-visit';

import Alert from '@r1-frontend/ui-react/components/Alert';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { Danger } from '@r1-frontend/ui-react/components/svg/main';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import CallbackContacts from '~/src/features/full-buy/CallbackContacts';
import { MainLayout } from '~/src/features/full-buy/containers/MainLayout/MainLayout';
import { isConnectionCostByVisitShown } from '~/src/features/full-buy/helpers/ab/isConnectionCostHidden';
import selectTimeSubText from '~/src/features/full-buy/helpers/selectTimeSubText';
import { setIsLoadingShown, setSlot } from '~/src/features/full-buy/store/actions';
import { goToNextStep } from '~/src/features/full-buy/store/actions/goToNextStep';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import TariffInclude from '~/src/features/full-buy/TariffInclude';
import { TState } from '~/src/store';
import { selectDomain } from '~/src/store/selectors/domain';

import * as S from '~/src/features/full-buy/containers/pages/styled';

const TIME_SLOT_YK_ERROR = 'Предварительное согласование с УК/ТСЖ';

enum ESlotsContainerState {
    SHOW_SLOTS = 1,
    HIDE_SLOTS = 2,
    SLOTS_EMPTY = 3
}

type TSlotResult = {
    state: ESlotsContainerState,
    result: TReturnType | null,
    errorMsg: string,
}

export const EngineerRequest = (): JSX.Element | null => {
    const analytics = useAnalytics();
    const dispatch = useDispatch();

    const isLaptop = useMatchMediaByWidth(wideBreakpoints.laptop);
    const [failureResponseFromSelectSlot, setFailureResponseFromSelectSlot] = useState<boolean>(false);
    const [slotResult, setSlotResult] = useState<TSlotResult>({ state: ESlotsContainerState.SHOW_SLOTS, result: null, errorMsg: '' });

    const [isTimeSlotLoading, setIsTimeSlotLoading] = useState(true);

    const {
        products,
        agreementId,
        domain,
        providerId,
        slot,
        selectedTariff,
    } = useSelector((state: TState) => ({
        products: state.fullBuy.products,
        agreementId: state.fullBuy.agreement.agreementId,
        domain: selectDomain(state),
        providerId: state.city.provider.providerId,
        slot: state.fullBuy.slot,
        selectedTariff: selectCurrentTariff(state),
    }), shallowEqual);

    const dateTime = slot?.date;

    const hasEquipment = useMemo(() => !!products?.some(el => el.quantity > 0), []);

    useEffect(() => {
        dispatch(setIsLoadingShown(isTimeSlotLoading));
    }, [isTimeSlotLoading]);

    useEffect(() => {
        if (slotResult.result !== null && slotResult.result.date && slotResult.result.hour && agreementId) {
            (async(slot: TReturnType, agreement: number) => {
                const requestData = {
                    agreementId: agreement,
                    dateFrom: `${slot.date} ${slot.hour}`,
                    saleAgentClientName: ESaleAgentClientName.FULL_BUY_SITE,
                    info: slot.comment,
                };

                const resp = await doRequest.fullBuy<ResponseDto>(
                    selectTimeSlot(providerId, requestData),
                );

                if (resp.isSuccess) {
                    batch(() => {
                        dispatch(setSlot(slot));
                        dispatch(goToNextStep());
                    });

                    analytics.timeSlots.success();
                } else {
                    setFailureResponseFromSelectSlot(true);

                    analytics.timeSlots.failed(resp.error.message, domain);
                }
            })(slotResult.result, agreementId);
        }
    }, [domain, agreementId, slotResult]);

    const handleConfirm = (result: TReturnType | null, errorMsg?: string) => {
        setSlotResult(prevState => ({
            ...prevState,
            state: result === null ? ESlotsContainerState.SLOTS_EMPTY : ESlotsContainerState.SHOW_SLOTS,
            result,
            errorMsg: errorMsg || '',
        }));
    };

    const onUpdate = () => {
        setIsTimeSlotLoading(false);
    };

    const getTimeSlots = (providerId: number, body: RequestDto) => {
        return doRequest.fullBuy<GetTimeSlotsResponseDto>(saleAgentTimeSlots(providerId, body));
    };

    if (!agreementId) {
        throw new Error('FullBuy Error. Required agreementId in this page!');
    }

    return (
        <MainLayout
            isHide={isTimeSlotLoading}
            showFinalTicket={(slotResult.state === ESlotsContainerState.SLOTS_EMPTY || failureResponseFromSelectSlot)}
        >
            <S.StageWrap>
                <H3 $withIndent={false}>Визит инженера</H3>
                {(slotResult.state === ESlotsContainerState.SHOW_SLOTS && !failureResponseFromSelectSlot) && (
                    <>
                        {isConnectionCostByVisitShown(!selectedTariff?.isCottage)
                            ? <>
                                <Paragraph4>
                                    Стоимость подключения составляет {selectedTariff?.connectionPrice.final}&nbsp;рублей. В&nbsp;эту сумму входят
                                    визит инженера, доставка и&nbsp;настройка оборудования, качественные материалы и&nbsp;прокладка восьмижильного
                                    кабеля длиной до&nbsp;5&nbsp;метров без ущерба для ремонта. Инженер поможет с&nbsp;настройкой услуг, онлайн
                                    оплатой, продемонстрирует работу услуг и&nbsp;ответит на&nbsp;ваши вопросы.
                                </Paragraph4>
                                <Paragraph4>
                                    Мы&nbsp;можем вам позвонить и&nbsp;уточнить детали. Убедитесь, что у&nbsp;вас отключен автоответчик
                                    и&nbsp;антиспам приложения.
                                </Paragraph4>
                            </>
                            : <Paragraph4>
                                {!hasEquipment && <>Инженер проложит кабель, подключит и&nbsp;настроит услуги.{'\n'}</>}
                                {selectTimeSubText(hasEquipment, false)}{'\n'}
                                Мы&nbsp;можем вам позвонить и&nbsp;уточнить детали. Убедитесь, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам
                                приложения.
                            </Paragraph4>
                        }
                    </>
                )}

                {slotResult.state === ESlotsContainerState.SLOTS_EMPTY &&
                    <Alert Icon={Danger} type={'success'}>
                        <Paragraph4>
                            {slotResult.errorMsg.includes(TIME_SLOT_YK_ERROR)
                                ? <>Мы&nbsp;свяжемся с&nbsp;вами в&nbsp;течение дня и&nbsp;поможем выбрать дату и&nbsp;время визита инженера.</>
                                : <>Не&nbsp;удалось подтвердить время и&nbsp;дату. Мы&nbsp;свяжемся с&nbsp;вами в&nbsp;течение дня и&nbsp;поможем
                                    выбрать их&nbsp;заново.
                                    Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам
                                    приложения.</>
                            }
                        </Paragraph4>
                    </Alert>
                }
                {failureResponseFromSelectSlot && (
                    <Alert Icon={Danger} type={'success'}>
                        <Paragraph4>
                            Не&nbsp;удалось подтвердить дату. Мы&nbsp;свяжемся с&nbsp;вами в&nbsp;течение дня и&nbsp;поможем выбрать её&nbsp;заново.
                            Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.
                        </Paragraph4>
                    </Alert>
                )}

                {(slotResult.state === ESlotsContainerState.SHOW_SLOTS && !failureResponseFromSelectSlot) &&
                    <DateOfServiceEngineerVisitWrapper
                        getTimeSlotsFunc={getTimeSlots}
                        onConfirm={handleConfirm}
                        onClickConfirm={() => analytics.timeSlots.start()}
                        contractId={agreementId}
                        clientName={ESaleAgentClientName.FULL_BUY_SITE}
                        withComment={true}
                        gaCategory={analytics.category}
                        domain={domain}
                        onUpdateLoading={onUpdate}
                    />
                }
                {(slotResult.state === ESlotsContainerState.SLOTS_EMPTY || failureResponseFromSelectSlot) && !isLaptop &&
                    <>
                        <TariffInclude showLink={!!dateTime}/>
                        <H3 $withIndent={false}>Если остались вопросы</H3>
                        <CallbackContacts/>
                    </>
                }
            </S.StageWrap>
        </MainLayout>
    );
};
